import { Icon } from '../../../../../../widgets/icon';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC, useMemo } from 'react';
import { TooltipContent } from 'src/components/pages/choosePlan/shared';
import { T } from 'src/shared/helpers';
import ArrowDownIcon from 'src/shared/icons/arrow-down-solid.svg';
import ArrowUpIcon from 'src/shared/icons/arrow-up-solid.svg';
import PreSetIcon from 'src/shared/icons/box-solid.svg';
import WarningIcon from 'src/shared/icons/exclamation-triangle-solid.svg';
import StepIcon from 'src/shared/icons/layer-group-solid.svg';
import LinkIcon from 'src/shared/icons/link-solid.svg';
import GroupIcon from 'src/shared/icons/object-ungroup.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { ComposerAttributeNode, ComposerAttributeType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

interface ComposerAttributeTreeElementContextMenuData {
    onContextMenuItemClick: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    contextMenu: React.ReactNode;
}

interface ComposerAttributeTreeElementProps {
    attribute: ComposerAttributeNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onToggleExpand?: (attribute: ComposerAttributeNode) => void;
    onMoveUp?: (attribute: ComposerAttributeNode) => void;
    onMoveDown?: (attribute: ComposerAttributeNode) => void;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
    contextMenu?: ComposerAttributeTreeElementContextMenuData;
    selected: boolean;
    hasLink?: boolean;
    expanded?: boolean;
    shift?: number;
    type: ComposerAttributeType;
    children?: React.ReactNode;
}

interface ComposerAttributeSubTreeElementProps {
    attribute: ComposerAttributeNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onToggleExpand?: (attribute: ComposerAttributeNode) => void;
    onMoveUp?: (attribute: ComposerAttributeNode) => void;
    onMoveDown?: (attribute: ComposerAttributeNode) => void;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
    contextMenu?: ComposerAttributeTreeElementContextMenuData;
    selected: boolean;
    shift?: number;
    hasLink?: boolean;
}

const ComposerAttributeTreeElementContainer = styled.div<{
    shift?: number;
    selected?: boolean;
}>`
    --shift: ${(props) => (props.shift ? `${props.shift * 24}px` : '0px')};
    display: flex;
    align-items: center;
    padding: 4px 8px;
    padding-left: 12px;
    margin-left: ${(props) => (props.shift ? 'var(--shift)' : '0px')};
    background-color: ${(props) => (props.selected ? theme.app.secondary : 'transparent')};
    border: 1px solid ${(props) => (props.selected ? theme.app.secondary : theme.app.borderColor)};
    color: ${(props) => (props.selected ? 'white' : theme.app.textColor)};
    border-radius: 4px;
    cursor: pointer;
    height: 48px;
    min-height: 48px;
    width: ${(props) => (props.shift ? 'calc(100% - var(--shift))' : '100%')};
    user-select: none;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        background-color: ${theme.app.backgroundColor};
        border-color: ${theme.app.secondary};
        opacity: ${(props) => (props.selected ? 1 : 0.8)};
    }
`;

const ComposerAttributeTreeElementHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    width: 100%;
    height: 100%;
    position: relative;
`;

const ComposerAttributeTreeElementLeftContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    width: calc(100% - 60px); /* Adjusted to account for right container */
    min-width: 0; /* Prevents flex item from overflowing */
`;

const ComposerAttributeTreeElementNameContainer = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ComposerAttributeTreeElementRightContainer = styled.div`
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    flex-shrink: 0; /* Prevents container from shrinking */
`;

const ComposerAttributeTreeElementExpanderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 24px;
    cursor: pointer;

    svg {
        width: 24px;
        height: 24px;
    }
`;

const IconContainer = styled.div`
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

const GetIconTooltip = (type: ComposerAttributeType) => {
    switch (type) {
        case ComposerAttributeType.AttributeStep:
            return T._('This is an attribute step', 'Admin');
        case ComposerAttributeType.AttributeGroup:
            return T._('This is an attribute group', 'Admin');
        case ComposerAttributeType.PreSetContainer:
            return T._('This is a pre-set container', 'Admin');
    }
};

const GetIconHeader = (type: ComposerAttributeType, selected: boolean = false) => {
    switch (type) {
        case ComposerAttributeType.Attribute:
            return <></>;
        case ComposerAttributeType.AttributeStep:
            return <StepIcon fill={selected ? 'white' : theme.app.textColor} />;
        case ComposerAttributeType.AttributeGroup:
            return <GroupIcon fill={selected ? 'white' : theme.app.textColor} />;
        case ComposerAttributeType.PreSetContainer:
            return <PreSetIcon fill={selected ? 'white' : theme.app.textColor} />;
        default:
            return <></>;
    }
};

export const ComposerAttributeTreeElement: FC<ComposerAttributeTreeElementProps> = ({
    attribute,
    onSelect,
    onDoubleClick,
    onToggleExpand,
    contextMenu,
    selected,
    hasLink,
    shift = 0,
    expanded,
    type,
    children,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDrop,
    onDragOver
}) => {
    const [showContextMenu, setShowContextMenu] = React.useState(false);

    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        if (contextMenu) {
            event.preventDefault();
            event.stopPropagation();
            setShowContextMenu(true);
            contextMenu.onContextMenuItemClick(event, attribute);
        }
    };

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onToggleExpand) {
            onToggleExpand(attribute);
        }
    };

    const hasBrokenOptions = useMemo(() => {
        return attribute.options.some((option) => option.brokenValues.size > 0);
    }, [attribute.options]);

    return (
        <>
            <ComposerAttributeTreeElementContainer
                shift={shift}
                selected={selected}
                onClick={(e) => onSelect(e, attribute)}
                onDoubleClick={onDoubleClick ? (e) => onDoubleClick(e, attribute) : undefined}
                onContextMenu={handleContextMenu}
                draggable={!!onDragStart}
                onDragStart={(e) => onDragStart && onDragStart(e, attribute)}
                onDragEnd={(e) => onDragEnd && onDragEnd(e, attribute)}
                onDragOver={onDragOver}
                onDrop={onDrop}
            >
                <ComposerAttributeTreeElementHeader>
                    {shift > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <hr style={{ height: '100%', border: '1px solid ' + theme.app.borderColor, position: 'absolute', left: '-32px' }} />
                        </div>
                    )}
                    <ComposerAttributeTreeElementLeftContainer>
                        {attribute.brokenValues.size > 0 ||
                            (hasBrokenOptions && (
                                <Icon
                                    tooltip={
                                        (attribute.brokenValues.values().map((value) => <TooltipContent key={value}>{value}</TooltipContent>),
                                        hasBrokenOptions && (
                                            <TooltipContent key='hasBrokenOptions'>{T._('This attribute has broken options', 'Admin')}</TooltipContent>
                                        ))
                                    }
                                >
                                    <WarningIcon fill={selected ? 'white' : theme.app.textColor} />
                                </Icon>
                            ))}
                        {children && (
                            <ComposerAttributeTreeElementExpanderContainer onClick={handleExpandClick}>
                                <IconContainer>
                                    {children &&
                                        (!expanded ? (
                                            <ArrowUpIcon fill={selected ? 'white' : theme.app.textColor} />
                                        ) : (
                                            <ArrowDownIcon fill={selected ? 'white' : theme.app.textColor} />
                                        ))}
                                </IconContainer>
                            </ComposerAttributeTreeElementExpanderContainer>
                        )}
                        <ZTooltip
                            title={GetIconTooltip(type)}
                            html={type !== ComposerAttributeType.Attribute && <TooltipContent>{GetIconTooltip(type)}</TooltipContent>}
                        >
                            <IconContainer>{GetIconHeader(type, selected)}</IconContainer>
                        </ZTooltip>
                        <ComposerAttributeTreeElementNameContainer title={attribute.name}>{attribute.name}</ComposerAttributeTreeElementNameContainer>
                    </ComposerAttributeTreeElementLeftContainer>

                    <ComposerAttributeTreeElementRightContainer>
                        {hasLink && (
                            <ZTooltip
                                title={T._('The attribute has constraints', 'Admin')}
                                html={<TooltipContent>{T._('The attribute has constraints', 'Admin')}</TooltipContent>}
                            >
                                <IconContainer>
                                    <LinkIcon fill={selected ? 'white' : theme.app.textColor} />
                                </IconContainer>
                            </ZTooltip>
                        )}
                        <IconContainer
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveUp && onMoveUp(attribute);
                            }}
                        >
                            <ArrowUpIcon fill={selected ? 'white' : theme.app.textColor} />
                        </IconContainer>
                        <IconContainer
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveDown && onMoveDown(attribute);
                            }}
                        >
                            <ArrowDownIcon fill={selected ? 'white' : theme.app.textColor} />
                        </IconContainer>
                    </ComposerAttributeTreeElementRightContainer>
                </ComposerAttributeTreeElementHeader>
                {showContextMenu && contextMenu && contextMenu.contextMenu}
            </ComposerAttributeTreeElementContainer>

            {!expanded && children}
        </>
    );
};

export const ComposerAttributeSubTreeElement: FC<ComposerAttributeSubTreeElementProps> = ({
    attribute,
    shift = 1,
    onSelect,
    onDoubleClick,
    onToggleExpand,
    contextMenu,
    selected,
    hasLink,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDrop,
    onDragOver
}) => {
    return (
        <ComposerAttributeTreeElement
            attribute={attribute}
            onSelect={(event) => onSelect(event, attribute)}
            onDoubleClick={onDoubleClick}
            onToggleExpand={onToggleExpand}
            contextMenu={contextMenu}
            selected={selected}
            hasLink={hasLink}
            type={ComposerAttributeType.Attribute}
            shift={shift}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDrop={onDrop}
            onDragOver={onDragOver}
        />
    );
};
